import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faServer,
  faPlus,
  faEdit,
  faTrash,
  faPowerOff,
  faCheck,
  faTimes,
  faArrowUp,
  faArrowDown,
  faSort,
  faSearch,
  faFilter,
  faCircle,
  faWifi,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminServers.css';

const AdminServers = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('priority');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingServer, setEditingServer] = useState(null);

  // Mock servers data
  const mockServers = [
    {
      id: 1,
      name: 'Server VIP',
      url: 'https://vip.example.com',
      priority: 1,
      status: 'active',
      type: 'primary',
      location: 'Singapore',
      bandwidth: '1Gbps',
      uptime: 99.9,
      created_at: '2024-01-15',
      last_check: '2024-01-20 14:30:00'
    },
    {
      id: 2,
      name: 'Server HD',
      url: 'https://hd.example.com',
      priority: 2,
      status: 'active',
      type: 'secondary',
      location: 'Japan',
      bandwidth: '500Mbps',
      uptime: 99.5,
      created_at: '2024-01-12',
      last_check: '2024-01-20 14:25:00'
    },
    {
      id: 3,
      name: 'Server FB',
      url: 'https://fb.example.com',
      priority: 3,
      status: 'active',
      type: 'backup',
      location: 'Vietnam',
      bandwidth: '200Mbps',
      uptime: 98.8,
      created_at: '2024-01-10',
      last_check: '2024-01-20 14:20:00'
    },
    {
      id: 4,
      name: 'Server Mirror',
      url: 'https://mirror.example.com',
      priority: 4,
      status: 'maintenance',
      type: 'backup',
      location: 'Korea',
      bandwidth: '300Mbps',
      uptime: 95.2,
      created_at: '2024-01-08',
      last_check: '2024-01-19 10:15:00'
    }
  ];

  const [filteredServers, setFilteredServers] = useState([]);

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setServers(mockServers);
      setFilteredServers(mockServers);
      setLoading(false);
    }, 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterAndSortServers();
  }, [searchTerm, filterStatus, sortField, sortDirection, servers]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterAndSortServers = () => {
    let filtered = [...servers];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(server =>
        server.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        server.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        server.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(server => server.status === filterStatus);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredServers(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddServer = () => {
    setEditingServer(null);
    setShowAddModal(true);
  };

  const handleEditServer = (server) => {
    setEditingServer(server);
    setShowAddModal(true);
  };

  const handleDeleteServer = (serverId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa server này?')) {
      setServers(servers.filter(server => server.id !== serverId));
    }
  };

  const handleToggleStatus = (serverId) => {
    setServers(servers.map(server => {
      if (server.id === serverId) {
        return {
          ...server,
          status: server.status === 'active' ? 'inactive' : 'active'
        };
      }
      return server;
    }));
  };

  const handleSaveServer = (serverData) => {
    if (editingServer) {
      // Update existing server
      setServers(servers.map(server => 
        server.id === editingServer.id 
          ? { ...server, ...serverData }
          : server
      ));
    } else {
      // Add new server
      const newServer = {
        id: Date.now(),
        ...serverData,
        created_at: new Date().toISOString().split('T')[0],
        last_check: new Date().toISOString(),
        uptime: 100.0
      };
      setServers([...servers, newServer]);
    }
    setShowAddModal(false);
    setEditingServer(null);
  };

  const handleCancelEdit = () => {
    setShowAddModal(false);
    setEditingServer(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'maintenance': return 'info';
      case 'error': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Hoạt động';
      case 'inactive': return 'Tạm dừng';
      case 'maintenance': return 'Bảo trì';
      case 'error': return 'Lỗi';
      default: return 'Không xác định';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'primary': return 'primary';
      case 'secondary': return 'info';
      case 'backup': return 'warning';
      default: return 'secondary';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'primary': return 'Chính';
      case 'secondary': return 'Phụ';
      case 'backup': return 'Dự phòng';
      default: return 'Khác';
    }
  };

  const formatUptime = (uptime) => {
    return `${uptime.toFixed(1)}%`;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  if (loading) {
    return (
      <div className="admin-servers">
        <div className="servers-loading">
          <div className="loading-spinner"></div>
          <p>Đang tải danh sách server...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-servers">
      <div className="servers-header">
        <div className="header-left">
          <h1>
            <FontAwesomeIcon icon={faServer} />
            Quản lý Server
          </h1>
          <p>Quản lý các server streaming và CDN</p>
        </div>
        <div className="header-actions">
          <button className="add-server-btn" onClick={handleAddServer}>
            <FontAwesomeIcon icon={faPlus} />
            Thêm Server
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="server-stats">
        <div className="stat-card">
          <div className="stat-icon success">
            <FontAwesomeIcon icon={faServer} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{servers.filter(s => s.status === 'active').length}</div>
            <div className="stat-label">Server Hoạt động</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">
            <FontAwesomeIcon icon={faPowerOff} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{servers.filter(s => s.status === 'inactive').length}</div>
            <div className="stat-label">Server Tạm dừng</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon info">
            <FontAwesomeIcon icon={faWifi} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{servers.filter(s => s.status === 'maintenance').length}</div>
            <div className="stat-label">Đang Bảo trì</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon primary">
            <FontAwesomeIcon icon={faGlobe} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{servers.length}</div>
            <div className="stat-label">Tổng Server</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="servers-filters">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Tìm kiếm server..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-select">
          <FontAwesomeIcon icon={faFilter} />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="active">Hoạt động</option>
            <option value="inactive">Tạm dừng</option>
            <option value="maintenance">Bảo trì</option>
            <option value="error">Lỗi</option>
          </select>
        </div>
      </div>

      {/* Servers Table */}
      <div className="servers-table-container">
        <table className="servers-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('priority')} className="sortable">
                Ưu tiên
                <FontAwesomeIcon 
                  icon={sortField === 'priority' ? (sortDirection === 'asc' ? faArrowUp : faArrowDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                Tên Server
                <FontAwesomeIcon 
                  icon={sortField === 'name' ? (sortDirection === 'asc' ? faArrowUp : faArrowDown) : faSort} 
                />
              </th>
              <th>URL</th>
              <th onClick={() => handleSort('type')} className="sortable">
                Loại
                <FontAwesomeIcon 
                  icon={sortField === 'type' ? (sortDirection === 'asc' ? faArrowUp : faArrowDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('location')} className="sortable">
                Vị trí
                <FontAwesomeIcon 
                  icon={sortField === 'location' ? (sortDirection === 'asc' ? faArrowUp : faArrowDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('bandwidth')} className="sortable">
                Băng thông
                <FontAwesomeIcon 
                  icon={sortField === 'bandwidth' ? (sortDirection === 'asc' ? faArrowUp : faArrowDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('uptime')} className="sortable">
                Uptime
                <FontAwesomeIcon 
                  icon={sortField === 'uptime' ? (sortDirection === 'asc' ? faArrowUp : faArrowDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Trạng thái
                <FontAwesomeIcon 
                  icon={sortField === 'status' ? (sortDirection === 'asc' ? faArrowUp : faArrowDown) : faSort} 
                />
              </th>
              <th>Kiểm tra cuối</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredServers.map(server => (
              <tr key={server.id}>
                <td>
                  <div className="priority-badge">
                    #{server.priority}
                  </div>
                </td>
                <td>
                  <div className="server-info">
                    <div className="server-name">{server.name}</div>
                  </div>
                </td>
                <td>
                  <div className="server-url">
                    <a href={server.url} target="_blank" rel="noopener noreferrer">
                      {server.url}
                    </a>
                  </div>
                </td>
                <td>
                  <div className={`type-badge ${getTypeColor(server.type)}`}>
                    {getTypeText(server.type)}
                  </div>
                </td>
                <td>
                  <div className="location-info">
                    <FontAwesomeIcon icon={faGlobe} />
                    {server.location}
                  </div>
                </td>
                <td>
                  <div className="bandwidth-info">
                    <FontAwesomeIcon icon={faWifi} />
                    {server.bandwidth}
                  </div>
                </td>
                <td>
                  <div className={`uptime-info ${server.uptime >= 99 ? 'good' : server.uptime >= 95 ? 'warning' : 'bad'}`}>
                    {formatUptime(server.uptime)}
                  </div>
                </td>
                <td>
                  <div className={`status-badge ${getStatusColor(server.status)}`}>
                    <FontAwesomeIcon icon={faCircle} />
                    {getStatusText(server.status)}
                  </div>
                </td>
                <td>
                  <div className="last-check">
                    {formatDateTime(server.last_check)}
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className={`action-btn toggle ${server.status === 'active' ? 'active' : 'inactive'}`}
                      title={server.status === 'active' ? 'Tạm dừng' : 'Kích hoạt'}
                      onClick={() => handleToggleStatus(server.id)}
                    >
                      <FontAwesomeIcon icon={server.status === 'active' ? faPowerOff : faCheck} />
                    </button>
                    <button 
                      className="action-btn edit"
                      title="Chỉnh sửa"
                      onClick={() => handleEditServer(server)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="action-btn delete"
                      title="Xóa"
                      onClick={() => handleDeleteServer(server.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Server Form Modal */}
      {showAddModal && (
        <ServerForm
          server={editingServer}
          onSave={handleSaveServer}
          onCancel={handleCancelEdit}
          isOpen={showAddModal}
        />
      )}
    </div>
  );
};

// Server Form Component
const ServerForm = ({ server, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    priority: 1,
    status: 'active',
    type: 'secondary',
    location: '',
    bandwidth: ''
  });

  useEffect(() => {
    if (server) {
      setFormData({
        name: server.name || '',
        url: server.url || '',
        priority: server.priority || 1,
        status: server.status || 'active',
        type: server.type || 'secondary',
        location: server.location || '',
        bandwidth: server.bandwidth || ''
      });
    } else {
      setFormData({
        name: '',
        url: '',
        priority: 1,
        status: 'active',
        type: 'secondary',
        location: '',
        bandwidth: ''
      });
    }
  }, [server]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content server-form-modal">
        <div className="modal-header">
          <h3>
            <FontAwesomeIcon icon={faServer} />
            {server ? 'Chỉnh sửa Server' : 'Thêm Server Mới'}
          </h3>
          <button className="modal-close" onClick={onCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="server-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Tên Server *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="VD: Server VIP"
                required
              />
            </div>

            <div className="form-group">
              <label>URL Server *</label>
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="https://example.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Ưu tiên</label>
              <input
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                min="1"
                max="100"
              />
            </div>

            <div className="form-group">
              <label>Loại Server</label>
              <select name="type" value={formData.type} onChange={handleChange}>
                <option value="primary">Chính</option>
                <option value="secondary">Phụ</option>
                <option value="backup">Dự phòng</option>
              </select>
            </div>

            <div className="form-group">
              <label>Vị trí</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="VD: Singapore"
              />
            </div>

            <div className="form-group">
              <label>Băng thông</label>
              <input
                type="text"
                name="bandwidth"
                value={formData.bandwidth}
                onChange={handleChange}
                placeholder="VD: 1Gbps"
              />
            </div>

            <div className="form-group">
              <label>Trạng thái</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Hoạt động</option>
                <option value="inactive">Tạm dừng</option>
                <option value="maintenance">Bảo trì</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              <FontAwesomeIcon icon={faTimes} />
              Hủy
            </button>
            <button type="submit" className="btn-save">
              <FontAwesomeIcon icon={faCheck} />
              {server ? 'Cập nhật' : 'Thêm mới'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminServers;